import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./lib/auth";
import { MerchantProvider } from "./app/contexts/MerchantContext";
import { CartProvider } from "./hooks/useCart";
import { Analytics } from "@vercel/analytics/react";
import App from "./app/App.tsx";
import "./styles/index.css";
import { initAnalytics } from "./lib/analytics";
import { registerSW } from 'virtual:pwa-register';

// Clear stale chunk-reload guards from previous sessions so auto-recovery
// is never permanently blocked by an old sessionStorage entry.
Object.keys(sessionStorage)
  .filter((k) => k.startsWith('chunk_reload_'))
  .forEach((k) => sessionStorage.removeItem(k));

// ── Sentry Initialization ────────────────────────────────────────────────────
Sentry.init({
  dsn: "https://0da4b8bd5ff3cfc0f733ce2ea77a7a3d@o4511139189882880.ingest.us.sentry.io/4511140051484672",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration()
  ],
  // Tracing
  tracesSampleRate: 1.0, // Capture 100% of the transactions (adjust for prod)
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/[^/]*\.supabase\.co\/(?!functions\/v1)/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // Set to 100% in dev if needed
  replaysOnErrorSampleRate: 1.0, // Always capture replays when errors occur
});

// Register PWA service worker with auto-reload on update
registerSW({ 
  immediate: true,
  onNeedRefresh() {
    window.location.reload();
  }
});

// Initialize analytics
initAnalytics();

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(
    <HelmetProvider>
      <AuthProvider>
        <MerchantProvider>
          <CartProvider>
            <App />
            <Analytics />
          </CartProvider>
        </MerchantProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}